import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Loader, Search, Sparkles, ChevronDown, ChevronUp } from 'lucide-react';
import PublicNav from '../components/PublicNav';
import Footer from '../components/Footer';
import { API_BASE_URL } from '../config';
import ExpertNoteByline from '../components/ExpertNoteByline';
import ExpertNoteFollowUpCta from '../components/ExpertNoteFollowUpCta';
import AiIdeasAnalysisSections from '../components/AiIdeasAnalysisSections';
import {
  getCommunityQuestions,
  postDesignerCommunityNote,
  getDesignerProfileForSession,
  designerProfileNoteLabel,
} from '../services/landscapingQuestionsApi';

function usePublicUserId() {
  try {
    const raw = localStorage.getItem('userData') || localStorage.getItem('user');
    if (!raw) return null;
    return JSON.parse(raw)?.id || null;
  } catch {
    return null;
  }
}

const PAGE_SIZE = 30;

export default function CommunityPage() {
  const currentUserId = usePublicUserId();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [qInput, setQInput] = useState('');
  const [cityInput, setCityInput] = useState('');
  const [stateInput, setStateInput] = useState('');
  const [applied, setApplied] = useState({ q: '', city: '', state: '' });
  const [listOffset, setListOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const [canPostNote, setCanPostNote] = useState(false);
  const [noteDrafts, setNoteDrafts] = useState({});
  const [noteSaving, setNoteSaving] = useState({});
  const [openNoteId, setOpenNoteId] = useState(null);
  const [defaultDesignerLabel, setDefaultDesignerLabel] = useState('');
  /** When true, show full AI answer + structured ideas (plants, design, etc.) for that question id. */
  const [expandedAiDetail, setExpandedAiDetail] = useState({});

  const fetchPage = useCallback(async (append, offset, filters) => {
    setLoading(true);
    setError(null);
    try {
      const list = await getCommunityQuestions({
        q: filters.q,
        city: filters.city,
        state: filters.state,
        limit: PAGE_SIZE,
        offset,
      });
      if (append) {
        setItems((prev) => [...prev, ...list]);
      } else {
        setItems(list);
      }
      setHasMore(list.length === PAGE_SIZE);
      setListOffset(offset + list.length);
    } catch (e) {
      setError(e.message || 'Could not load Community.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    document.title = 'Community | PlantaDay';
  }, []);

  useEffect(() => {
    fetchPage(false, 0, applied);
  }, [applied, fetchPage]);

  useEffect(() => {
    const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
    if (!token) {
      setCanPostNote(false);
      return;
    }
    let cancelled = false;
    fetch(`${API_BASE_URL}/designers/community-questions?limit=1`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => {
        if (!cancelled) setCanPostNote(r.ok);
      })
      .catch(() => {
        if (!cancelled) setCanPostNote(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!canPostNote) return;
    let cancelled = false;
    getDesignerProfileForSession()
      .then((p) => {
        if (!cancelled) setDefaultDesignerLabel(designerProfileNoteLabel(p));
      })
      .catch(() => {
        if (!cancelled) setDefaultDesignerLabel('');
      });
    return () => {
      cancelled = true;
    };
  }, [canPostNote]);

  const handleSearch = (e) => {
    e.preventDefault();
    setApplied({
      q: qInput.trim(),
      city: cityInput.trim(),
      state: stateInput.trim(),
    });
  };

  const handleLoadMore = () => {
    if (!loading && hasMore) fetchPage(true, listOffset, applied);
  };

  const handlePostNote = async (questionId) => {
    const draft = noteDrafts[questionId] || {};
    const text = (draft.text || '').trim();
    if (!text) return;
    setNoteSaving((s) => ({ ...s, [questionId]: true }));
    try {
      const userData = JSON.parse(localStorage.getItem('userData') || localStorage.getItem('user') || '{}');
      const label =
        (draft.label || '').trim() ||
        defaultDesignerLabel ||
        userData.display_name ||
        userData.fullName ||
        userData.full_name ||
        'PlantADay Designer';
      await postDesignerCommunityNote(questionId, {
        advice_text: text,
        contributor_label: label,
      });
      setNoteDrafts((d) => ({ ...d, [questionId]: { text: '', label: draft.label || '' } }));
      setOpenNoteId(null);
      await fetchPage(false, 0, applied);
    } catch (err) {
      alert(err.message || 'Could not submit note');
    } finally {
      setNoteSaving((s) => ({ ...s, [questionId]: false }));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <PublicNav currentUserId={currentUserId} />
      <main className="flex-1 max-w-3xl mx-auto px-4 py-10 sm:py-14 w-full">
        <h1 className="text-3xl font-bold text-gray-900">Community</h1>
        <p className="mt-3 text-gray-600 leading-relaxed">
          Real yard questions from Get Ideas (opt-in, anonymous). Expand a post to see the full AI write-up plus plant
          suggestions, design ideas, and next steps — the same detail the homeowner received. Designers can add notes
          below.
        </p>

        <form onSubmit={handleSearch} className="mt-8 space-y-3">
          <div>
            <label htmlFor="comm-q" className="block text-xs font-medium text-gray-500 mb-1">
              Search questions
            </label>
            <input
              id="comm-q"
              type="search"
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
              placeholder="e.g. privacy, shade, clay soil"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label htmlFor="comm-city" className="block text-xs font-medium text-gray-500 mb-1">
                City
              </label>
              <input
                id="comm-city"
                type="text"
                value={cityInput}
                onChange={(e) => setCityInput(e.target.value)}
                placeholder="City"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
            <div>
              <label htmlFor="comm-state" className="block text-xs font-medium text-gray-500 mb-1">
                State / province
              </label>
              <input
                id="comm-state"
                type="text"
                value={stateInput}
                onChange={(e) => setStateInput(e.target.value)}
                placeholder="State or province"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
          </div>
          <button
            type="submit"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-semibold hover:bg-green-700"
          >
            <Search className="w-4 h-4" />
            Search
          </button>
        </form>

        {loading && items.length === 0 && (
          <div className="flex items-center gap-2 text-gray-500 py-12">
            <Loader className="w-6 h-6 animate-spin" /> Loading…
          </div>
        )}

        {error && (
          <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-900 text-sm">{error}</div>
        )}

        {!loading && !error && items.length === 0 && (
          <p className="mt-10 text-gray-600 text-sm">
            No Community posts yet. On{' '}
            <Link to="/ideas" className="text-green-700 font-medium hover:underline">
              Get Ideas
            </Link>
            , opt in to include your question here (anonymous).
          </p>
        )}

        <ul className="mt-10 space-y-6">
          {items.map((row) => (
            <li
              key={row.id}
              className="rounded-2xl border border-gray-200 bg-white p-5 sm:p-6 shadow-sm"
            >
              <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 mb-3">
                {(row.city_text || row.state_text) && (
                  <span className="px-2 py-0.5 rounded-full bg-green-50 text-green-800 font-medium">
                    {[row.city_text, row.state_text].filter(Boolean).join(', ')}
                  </span>
                )}
                {row.has_photo && (
                  <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">Photo</span>
                )}
                {row.created_at && (
                  <span>{new Date(row.created_at).toLocaleDateString(undefined, { dateStyle: 'medium' })}</span>
                )}
                <span className="text-gray-400">Anonymous</span>
              </div>
              <p className="text-gray-900 font-medium leading-snug">{row.question_text}</p>
              {!expandedAiDetail[row.id] && row.ai_answer_excerpt && (
                <p className="mt-3 text-sm text-gray-600 leading-relaxed whitespace-pre-wrap">{row.ai_answer_excerpt}</p>
              )}

              {(row.ai_answer || row.analysis) && (
                <div className="mt-3">
                  <button
                    type="button"
                    onClick={() =>
                      setExpandedAiDetail((e) => ({ ...e, [row.id]: !e[row.id] }))
                    }
                    className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-800 hover:text-emerald-950"
                  >
                    {expandedAiDetail[row.id] ? (
                      <>
                        <ChevronUp className="w-4 h-4" aria-hidden />
                        Hide full AI ideas
                      </>
                    ) : (
                      <>
                        <ChevronDown className="w-4 h-4" aria-hidden />
                        Show full AI ideas (plants, design, next steps)
                      </>
                    )}
                  </button>
                </div>
              )}

              {expandedAiDetail[row.id] && (
                <div className="mt-4 space-y-4">
                  {row.ai_answer && (
                    <div className="rounded-xl border-2 border-emerald-200 bg-emerald-50/80 p-4 sm:p-5">
                      <div className="flex items-center gap-2 text-emerald-900 mb-2">
                        <Sparkles className="w-5 h-5 flex-shrink-0" aria-hidden />
                        <h2 className="text-base font-bold">AI ideas</h2>
                      </div>
                      <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">{row.ai_answer}</p>
                      <p className="mt-3 text-xs text-emerald-800/70">
                        Generated by AI. Verify important decisions with local pros and on-site conditions.
                      </p>
                    </div>
                  )}
                  <AiIdeasAnalysisSections
                    analysis={row.analysis}
                    matchedPlants={row.matched_plants || []}
                  />
                </div>
              )}

              <div className="mt-4">
                <Link
                  to={`/ask-ai/${row.id}`}
                  className="text-sm font-semibold text-green-700 hover:underline"
                >
                  Open public answer page →
                </Link>
              </div>

              {row.expert_notes?.length > 0 && (
                <div className="mt-4 space-y-2">
                  {row.expert_notes.map((n) => (
                    <div
                      key={n.id}
                      className="rounded-xl border border-teal-200 bg-teal-50/80 px-3 py-2 text-sm text-teal-950"
                    >
                      {n.contributor_label && (
                        <div className="text-xs font-semibold text-teal-800 mb-1">
                          <ExpertNoteByline
                            note={n}
                            className="text-xs font-semibold text-teal-800"
                            linkClassName="text-teal-900 hover:underline font-semibold"
                          />
                        </div>
                      )}
                      <p className="whitespace-pre-wrap">{n.advice_text}</p>
                      <ExpertNoteFollowUpCta note={n} className="text-xs text-teal-900/85 mt-2 leading-snug" />
                    </div>
                  ))}
                </div>
              )}

              {canPostNote && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  {openNoteId === row.id ? (
                    <div className="space-y-2">
                      <label className="block text-xs font-medium text-gray-600">Designer note (sent for review)</label>
                      <textarea
                        value={(noteDrafts[row.id]?.text) || ''}
                        onChange={(e) =>
                          setNoteDrafts((d) => ({
                            ...d,
                            [row.id]: { ...d[row.id], text: e.target.value },
                          }))
                        }
                        rows={4}
                        placeholder="Add practical advice for this yard…"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                      <input
                        type="text"
                        value={(noteDrafts[row.id]?.label) || ''}
                        onChange={(e) =>
                          setNoteDrafts((d) => ({
                            ...d,
                            [row.id]: { ...d[row.id], label: e.target.value },
                          }))
                        }
                        placeholder="Your name or business (optional)"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          disabled={noteSaving[row.id]}
                          onClick={() => handlePostNote(row.id)}
                          className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50"
                        >
                          {noteSaving[row.id] ? 'Sending…' : 'Submit note'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setOpenNoteId(null)}
                          className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        setOpenNoteId(row.id);
                        setNoteDrafts((d) => {
                          const cur = d[row.id] || {};
                          const nextLabel = (String(cur.label || '').trim()
                            ? cur.label
                            : defaultDesignerLabel) || '';
                          return { ...d, [row.id]: { ...cur, label: nextLabel } };
                        });
                      }}
                      className="text-sm font-semibold text-blue-700 hover:underline"
                    >
                      Add a designer note
                    </button>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>

        {hasMore && items.length > 0 && (
          <div className="mt-8 flex justify-center">
            <button
              type="button"
              disabled={loading}
              onClick={handleLoadMore}
              className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-50"
            >
              {loading ? 'Loading…' : 'Load more'}
            </button>
          </div>
        )}

        <p className="mt-10 text-sm text-gray-500">
          <Link to="/ideas" className="text-green-700 font-medium hover:underline">
            Get Ideas
          </Link>
          {' · '}
          <Link to="/" className="text-green-700 font-medium hover:underline">
            Home
          </Link>
        </p>
      </main>
      <Footer />
    </div>
  );
}
